import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { Banner, ShippingRule, SeoConfig, User, BannerZone, Category, FlashDealConfig, SocialProofConfig, EmailTemplate, EmailCampaign } from '../domain/types';
import { marketingService } from '../domain/services/marketingService';
import { useAuth } from './auth';
import { logger } from '../infrastructure/logger';

// OPTIMIZATION: We use internal split contexts to prevent "God Object" re-renders
// but keep them in the same file for architectural simplicity as per project preference.
interface MarketingDataContextType {
    banners: Banner[];
    categories: Category[];
    isLoading: boolean;
    getBannersByZone: (zone: BannerZone, user: User | null) => Banner[];
    toggleBanner: (id: string) => void;
    addBanner: (banner: Banner) => Promise<void>;
    updateBanner: (banner: Banner) => Promise<void>;
    deleteBanner: (id: string) => Promise<void>;
    addCategory: (category: Category) => Promise<void>;
    updateCategory: (category: Category) => Promise<void>;
    deleteCategory: (id: string) => Promise<void>;
}

interface MarketingConfigContextType {
    shippingRule: ShippingRule;
    shippingCosts: { local: number, national: number };
    seoConfig: SeoConfig;
    flashDeals: FlashDealConfig;
    socialProof: SocialProofConfig;
    pickupAddress: string;
    emailTemplates: EmailTemplate[];
    emailCampaigns: EmailCampaign[];
    updateShippingRule: (rule: Partial<ShippingRule>) => void;
    updateShippingCosts: (costs: Partial<{ local: number, national: number }>) => void;
    updateSeoConfig: (config: Partial<SeoConfig>) => void;
    updateFlashDeals: (config: Partial<FlashDealConfig>) => void;
    updateSocialProof: (config: Partial<SocialProofConfig>) => void;
    updatePickupAddress: (address: string) => void;
    saveEmailTemplate: (template: EmailTemplate) => Promise<void>;
    saveEmailCampaign: (campaign: EmailCampaign) => Promise<void>;
}

const MarketingDataContext = createContext<MarketingDataContextType | null>(null);
const MarketingConfigContext = createContext<MarketingConfigContextType | null>(null);

const DEFAULT_TEMPLATES: EmailTemplate[] = [
    {
        id: 'welcome',
        name: 'Bienvenida & Confirmación',
        subject: '¡Hola! Bienvenido a la comunidad de AllTuc',
        content: `<div style="font-family: sans-serif; padding: 20px; color: #333; max-width: 600px; margin: 0 auto; border: 1px solid #eee; border-radius: 10px;">
  <h1 style="color: #e66e19;">¡Bienvenido a AllTuc!</h1>
  <p>Hola <strong>{{name}}</strong>,</p>
  <p>Tu cuenta ha sido creada con éxito. Ya puedes empezar a disfrutar de los beneficios del Club.</p>
  <div style="text-align: center; margin: 30px 0;">
    <a href="https://alltuc.com" style="background-color: #e66e19; color: white; padding: 12px 25px; text-decoration: none; border-radius: 5px; font-weight: bold;">Explorar Catálogo</a>
  </div>
  <p style="font-size: 12px; color: #999;">Si no creaste esta cuenta, ignora este mensaje.</p>
</div>`
    },
    {
        id: 'order_confirm',
        name: 'Confirmación de Pedido',
        subject: 'Tu pedido #{{orderId}} está confirmado',
        content: `<div style="font-family: sans-serif; padding: 20px; color: #333; max-width: 600px; margin: 0 auto; border: 1px solid #eee; border-radius: 10px;">
  <h2 style="color: #333;">¡Gracias por tu compra!</h2>
  <p>Hola <strong>{{name}}</strong>, hemos recibido tu pedido correctamente.</p>
  <div style="background: #f9f9f9; padding: 15px; border-radius: 8px; margin: 20px 0;">
    <p style="margin: 0; font-weight: bold;">Pedido #{{orderId}}</p>
    <p style="margin: 5px 0 0; color: #666;">Total: $ {{total}}</p>
  </div>
  <p>Te avisaremos cuando tu pedido esté en camino.</p>
  <div style="text-align: center; margin: 30px 0;">
    <a href="https://alltuc.com/#/tracking" style="background-color: #333; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px; font-weight: bold;">Seguir Envío</a>
  </div>
</div>`
    }
];

const DEFAULT_SOCIAL_PROOF: SocialProofConfig = {
    enabled: false,
    position: 'bottom-left',
    displayMode: 'inside',
    intervalMin: 8,
    intervalMax: 18,
    messages: []
};

const DEFAULT_FLASH_DEALS: FlashDealConfig = {
    enabled: true,
    endTime: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // 24 horas desde ahora
    discountPercentage: 20,
    productIds: [] // Se llenará dinámicamente con productos destacados
};

export const MarketingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [emailCampaigns, setEmailCampaigns] = useState<EmailCampaign[]>([]);
  const { user, isAdmin, isSuperAdmin } = useAuth();
  
  const [shippingRule, setShippingRule] = useState<ShippingRule>({ minAmountForFreeShipping: 0, freeShippingCategories: [] });
  const [shippingCosts, setShippingCosts] = useState({ local: 0, national: 0 });
  const [seoConfig, setSeoConfig] = useState<SeoConfig>({ siteTitle: 'AllTuc', siteDescription: '' });
  const [flashDeals, setFlashDeals] = useState<FlashDealConfig>(DEFAULT_FLASH_DEALS);
  const [socialProof, setSocialProof] = useState<SocialProofConfig>(DEFAULT_SOCIAL_PROOF);
  const [pickupAddress, setPickupAddress] = useState('San Martín 650, San Miguel de Tucumán');
  const [isLoading, setIsLoading] = useState(true);

    const loadData = useCallback(async () => {
        try {
            const [fetchedBanners, fetchedCategories, config] = await Promise.all([
                marketingService.getBanners(),
                marketingService.getCategories(),
                marketingService.getConfig()
            ]);
            
            setBanners(fetchedBanners || []);
            setCategories(fetchedCategories || []);
            setShippingRule(config?.shippingRule || { minAmountForFreeShipping: 0, freeShippingCategories: [] });
            setShippingCosts(config?.shippingCosts || { local: 0, national: 0 });
            setSeoConfig(config?.seo || { siteTitle: 'AllTuc', siteDescription: '' });
            setFlashDeals(config?.flashDeals || DEFAULT_FLASH_DEALS);
            setSocialProof(config?.socialProof || DEFAULT_SOCIAL_PROOF);
            setPickupAddress(config?.pickupAddress || 'San Martín 650, San Miguel de Tucumán');

            if (user && (isAdmin || isSuperAdmin)) {
                try {
                    const [fetchedTemplates, fetchedCampaigns] = await Promise.all([
                        marketingService.getTemplates(),
                        marketingService.getCampaigns()
                    ]);
                    
                    setEmailTemplates(fetchedTemplates.length > 0 ? fetchedTemplates : DEFAULT_TEMPLATES);
                    setEmailCampaigns(fetchedCampaigns || []);
                } catch {
                    setEmailTemplates(DEFAULT_TEMPLATES);
                }
            } else {
                setEmailTemplates(DEFAULT_TEMPLATES);
            }
        } catch (error) {
            logger.error("Error loading marketing data", error instanceof Error ? error : new Error(String(error)));
        } finally {
            setIsLoading(false);
        }
    }, [user]);

    useEffect(() => {
        loadData();
        const unsubscribe = marketingService.subscribeToConfig((config) => {
            if (config) {
                setShippingRule(config.shippingRule || { minAmountForFreeShipping: 0, freeShippingCategories: [] });
                setShippingCosts(config.shippingCosts || { local: 0, national: 0 });
                setSeoConfig(config.seo || { siteTitle: 'AllTuc', siteDescription: '' });
                setFlashDeals(config.flashDeals || DEFAULT_FLASH_DEALS);
                setSocialProof(config.socialProof || DEFAULT_SOCIAL_PROOF);
                setPickupAddress(config.pickupAddress || 'San Martín 650, San Miguel de Tucumán');
            }
        });
        return () => unsubscribe();
    }, [loadData]);

  const saveEmailTemplate = useCallback(async (template: EmailTemplate) => {
      await marketingService.saveTemplate(template);
      loadData();
  }, [loadData]);

  const saveEmailCampaign = useCallback(async (campaign: EmailCampaign) => {
      await marketingService.saveCampaign(campaign);
      loadData();
  }, [loadData]);

  const toggleBanner = useCallback(async (id: string) => {
      const banner = banners.find(b => b.id === id);
      if (banner) {
          const success = await marketingService.toggleBanner(id, banner.isActive);
          if (success) loadData(); 
      }
  }, [banners, loadData]);

  const addBanner = useCallback(async (banner: Banner) => {
      await marketingService.saveBanner(banner);
      loadData();
  }, [loadData]);
  
  const updateBanner = useCallback(async (banner: Banner) => {
      await marketingService.saveBanner(banner);
      loadData();
  }, [loadData]);

  const deleteBanner = useCallback(async (id: string) => {
      await marketingService.deleteBanner(id);
      loadData();
  }, [loadData]);

  const getBannersByZone = useCallback((zone: BannerZone, user: User | null) => {
      return banners.filter(b => b.zone === zone && b.isActive && (b.targetAudience === 'all' || (user ? b.targetAudience === 'member' : b.targetAudience === 'guest')));
  }, [banners]);

  const addCategory = useCallback(async (category: Category) => {
      await marketingService.saveCategory(category);
      loadData();
  }, [loadData]);

  const updateCategory = useCallback(async (category: Category) => {
      await marketingService.saveCategory(category);
      loadData();
  }, [loadData]);

  const deleteCategory = useCallback(async (id: string) => {
      await marketingService.deleteCategory(id);
      loadData();
  }, [loadData]);

  const updateShippingRule = useCallback(async (newRule: Partial<ShippingRule>) => {
      await marketingService.updateShippingRules(newRule);
      loadData();
  }, [loadData]);

  const updateShippingCosts = useCallback(async (costs: Partial<{ local: number, national: number }>) => {
      await marketingService.updateCosts(costs);
      loadData();
  }, [loadData]);

  const updateSeoConfig = useCallback(async (config: Partial<SeoConfig>) => {
      await marketingService.updateSEO(config);
      loadData();
  }, [loadData]);

  const updateFlashDeals = useCallback(async (config: Partial<FlashDealConfig>) => {
      await marketingService.updateFlashDeals(config);
      loadData();
  }, [loadData]);

  const updateSocialProof = useCallback(async (config: Partial<SocialProofConfig>) => {
      await marketingService.updateSocialProof(config);
      loadData();
  }, [loadData]);

  const updatePickupAddress = useCallback(async (address: string) => {
      await marketingService.updatePickupAddress(address);
      loadData();
  }, [loadData]);

  const dataValue = useMemo(() => ({ 
        banners, categories, isLoading, toggleBanner, addBanner, updateBanner, deleteBanner, getBannersByZone, addCategory, updateCategory, deleteCategory 
    }), [banners, categories, isLoading, toggleBanner, addBanner, updateBanner, deleteBanner, getBannersByZone, addCategory, updateCategory, deleteCategory]);

  const configValue = useMemo(() => ({
        shippingRule, shippingCosts, seoConfig, flashDeals, socialProof, pickupAddress, emailTemplates, emailCampaigns,
        updateShippingRule, updateShippingCosts, updateSeoConfig, updateFlashDeals, updateSocialProof, updatePickupAddress, saveEmailTemplate, saveEmailCampaign
  }), [shippingRule, shippingCosts, seoConfig, flashDeals, socialProof, pickupAddress, emailTemplates, emailCampaigns, updateShippingRule, updateShippingCosts, updateSeoConfig, updateFlashDeals, updateSocialProof, updatePickupAddress, saveEmailTemplate, saveEmailCampaign]);

  return (
    <MarketingDataContext.Provider value={dataValue}>
        <MarketingConfigContext.Provider value={configValue}>
            {children}
        </MarketingConfigContext.Provider>
    </MarketingDataContext.Provider>
  );
};

// Internal split hooks for performance (HomeScreen uses Data, Checkout uses Config)
export const useMarketingData = () => {
    const context = useContext(MarketingDataContext);
    if (!context) throw new Error("useMarketingData must be used within MarketingProvider");
    return context;
};

export const useMarketingConfig = () => {
    const context = useContext(MarketingConfigContext);
    if (!context) throw new Error("useMarketingConfig must be used within MarketingProvider");
    return context;
};

// Legacy hook for backward compatibility
export const useMarketing = () => {
  const data = useMarketingData();
  const config = useMarketingConfig();
  return { ...data, ...config };
};
